/* eslint-disable @typescript-eslint/no-explicit-any */
import qs from "qs";
import type { FetchOptions } from "ofetch";
import { useRuntimeConfig, useRequestHeaders, useCookie, createError } from "#imports";

/* -------------------------------------------------------------------------- */
/* Types */
/* -------------------------------------------------------------------------- */

type BaseTarget = "api" | "local";
type HttpMethod = Uppercase<"get" | "post" | "put" | "patch" | "delete" | "head" | "options">;
type CsrfMode = "auto" | "off" | "force";

type UntypedFetch = <T = unknown>(input: string, opts?: FetchOptions<"json">) => Promise<T>;
declare const $fetch: UntypedFetch;
const ufetch = $fetch;

export type SmartFetchOptions = Omit<FetchOptions<"json">, "query" | "params"> & {
    query?: Record<string, any>;
    params?: Record<string, any>;
    baseTarget?: BaseTarget;
    asForm?: boolean;
    timeoutMs?: number;
    csrf?: CsrfMode; // "auto" | "off" | "force"
    primeCsrf?: boolean; // optionally hit /sanctum/csrf-cookie first
    sanctumBase?: string; // override base for csrf-cookie priming
    retry?: {
        attempts?: number;
        methods?: HttpMethod[];
        statusCodes?: number[];
        baseDelayMs?: number;
        jitter?: boolean;
    };
    cache?: { enabled?: boolean; ttlMs?: number };
};

/* -------------------------------------------------------------------------- */
/* Helpers */
/* -------------------------------------------------------------------------- */

const ABS_RE = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;
const isAbsoluteUrl = (u: string) => ABS_RE.test(u);

const isUploadLike = (v: any) =>
    (typeof FormData !== "undefined" && v instanceof FormData) ||
    (typeof Blob !== "undefined" && v instanceof Blob) ||
    (typeof ArrayBuffer !== "undefined" && v instanceof ArrayBuffer);

const sanitizeObject = (obj: Record<string, any>) => {
    const out: Record<string, any> = {};
    for (const k in obj) {
        const val = obj[k];
        if (val === undefined) continue;
        out[k] = val instanceof Date ? val.toISOString() : val;
    }
    return out;
};

const joinPath = (base: string, path: string) => {
    if (!base) return path;
    const b = base.endsWith("/") ? base.slice(0, -1) : base;
    const p = path.startsWith("/") ? path.slice(1) : path;
    return `${b}/${p}`;
};

const getCache = new Map<string, { expiry: number; value: any }>();

function buildUrlWithMergedQuery(input: string, baseURL: string, mergedQuery: Record<string, any>) {
    const absolute = isAbsoluteUrl(input);
    const raw = absolute ? input : joinPath(baseURL, input);
    const url = new URL(raw, "http://dummy.base");
    const existingParams = qs.parse(url.search.replace(/^\?/, ""));
    const finalQuery = { ...(existingParams as any), ...mergedQuery };

    const queryString = Object.keys(finalQuery).length
        ? `?${qs.stringify(finalQuery, {
                encodeValuesOnly: true,
                skipNulls: true,
            })}`
        : "";

    return `${url.origin}${url.pathname}${queryString}`;
}

function jitteredDelay(ms: number, jitter: boolean) {
    if (!jitter) return ms;
    const delta = ms * 0.2;
    const min = ms - delta;
    const max = ms + delta;
    return Math.floor(min + Math.random() * (max - min));
}

/* -------------------------------------------------------------------------- */
/* CSRF priming helper */
/* -------------------------------------------------------------------------- */

/**
 * Fetches /sanctum/csrf-cookie to set XSRF-TOKEN + session cookies.
 * Safe to call multiple times; failures are swallowed.
 */
export async function primeCsrfCookie(base: string) {
    try {
        await ufetch<null>(joinPath(base, "/sanctum/csrf-cookie"), {
            method: "GET",
            credentials: "include",
            headers: {
                "X-Requested-With": "XMLHttpRequest",
                Accept: "application/json",
            },
        });
    } catch {
        // silently ignore; main request will show the real error if it fails
    }
}

/* -------------------------------------------------------------------------- */
/* Main smartFetch */
/* -------------------------------------------------------------------------- */

export async function smartFetch<T = unknown>(input: string, opts: SmartFetchOptions = {}): Promise<T> {
    const {
        public: { apiBase, localBase },
    } = useRuntimeConfig();

    const {
        baseTarget = "api",
        asForm = false,
        method,
        query,
        params,
        body,
        headers,
        timeoutMs = 15000,
        retry,
        cache,
        csrf = "auto",
        primeCsrf = false,
        credentials,
        sanctumBase,
        ...rest
    } = opts;

    const upperMethod = ((method || "GET") as string).toUpperCase() as HttpMethod;

    // Fix precedence for baseURL fallback
    const baseURL: string = opts.baseURL ?? (isAbsoluteUrl(input) ? "" : baseTarget === "local" ? localBase : apiBase);

    // Optionally prime CSRF cookie before request
    if (primeCsrf) {
        const primeBase = sanctumBase ?? (baseURL || (baseTarget === "local" ? localBase : apiBase));
        if (primeBase) {
            await primeCsrfCookie(primeBase);
        }
    }

    // SSR: forward incoming cookies/authorization
    const serverHeaders = import.meta.server ? useRequestHeaders(["cookie", "authorization"]) : {};
    const finalHeaders = new Headers({
        ...serverHeaders,
        ...(headers as Record<string, string> | undefined),
    });

    if (!finalHeaders.has("x-request-id")) {
        finalHeaders.set("x-request-id", crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
    }

    // Merge query + GET-body
    const mq: Record<string, any> = sanitizeObject({
        ...(query && typeof query === "object" ? query : {}),
        ...(params && typeof params === "object" ? params : {}),
    });
    if (upperMethod === "GET" && body && typeof body === "object" && !isUploadLike(body)) {
        Object.assign(mq, sanitizeObject(body as Record<string, any>));
    }

    const url = buildUrlWithMergedQuery(input, baseURL, mq);

    // Body handling
    let finalBody: any = body;
    if (upperMethod !== "GET" && body && typeof body === "object" && !isUploadLike(body)) {
        if (asForm) {
            finalBody = qs.stringify(sanitizeObject(body as Record<string, any>), {
                arrayFormat: "brackets",
                encodeValuesOnly: true,
                skipNulls: true,
            });
            if (!finalHeaders.has("Content-Type")) {
                finalHeaders.set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
            }
        }
    }

    // Attach CSRF header if policy says so
    const shouldAttachCsrf = csrf === "force" || (csrf === "auto" && upperMethod !== "GET");
    if (shouldAttachCsrf) {
        const xsrfCookie = useCookie<string | null>("XSRF-TOKEN").value;
        if (xsrfCookie) {
            finalHeaders.set("X-Requested-With", "XMLHttpRequest");
            finalHeaders.set("Accept", "application/json");
            finalHeaders.set("X-XSRF-TOKEN", decodeURIComponent(xsrfCookie));
        }
    }

    // Timeout / Abort
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(new DOMException("Timeout", "AbortError")), timeoutMs);

    // Basic GET cache
    const enableCache = cache?.enabled && upperMethod === "GET";
    const ttlMs = cache?.ttlMs ?? 5000;
    const cacheKey = enableCache ? `${upperMethod}:${url}` : null;
    if (enableCache && cacheKey) {
        const hit = getCache.get(cacheKey);
        if (hit && hit.expiry > Date.now()) {
            clearTimeout(t);
            return hit.value;
        }
    }

    // Retry policy
    const attempts = retry?.attempts ?? 0;
    const retryMethods = (retry?.methods ?? (["GET", "HEAD"] as HttpMethod[])).map((m: string) => m.toUpperCase() as HttpMethod);
    const retryOn = new Set(retry?.statusCodes ?? [408, 429, 500, 502, 503, 504]);
    const baseDelay = retry?.baseDelayMs ?? 300;
    const useJitter = retry?.jitter ?? true;

    let lastErr: any;
    for (let i = 0; i <= attempts; i++) {
        try {
            const res = await ufetch<T>(url, {
                method: upperMethod,
                headers: finalHeaders,
                body: upperMethod === "GET" ? undefined : finalBody,
                signal: ac.signal,
                credentials: shouldAttachCsrf ? "include" : credentials,
                onRequest({ options }) {
                    options.headers = finalHeaders;
                },
                onResponse() {},
                onRequestError() {},
                onResponseError({ response }) {
                    throw createError({
                        statusCode: response.status,
                        statusMessage: response.statusText,
                        data: response._data,
                    });
                },
                ...(rest as FetchOptions<"json">),
            });

            clearTimeout(t);

            if (enableCache && cacheKey) {
                getCache.set(cacheKey, { expiry: Date.now() + ttlMs, value: res });
            }
            return res;
        } catch (err: any) {
            lastErr = err;
            const status = err?.statusCode ?? err?.response?.status;
            const methodRetryable = retryMethods.includes(upperMethod);
            const statusRetryable = status ? retryOn.has(status) : true;

            if (i < attempts && methodRetryable && statusRetryable) {
                const delay = jitteredDelay(baseDelay * Math.pow(2, i), useJitter);
                await new Promise((r) => setTimeout(r, delay));
                continue;
            }
            clearTimeout(t);

            const requestId = err?.response?.headers?.get?.("x-request-id") || finalHeaders.get("x-request-id");
            throw createError({
                statusCode: status || 0,
                statusMessage: err?.message || "Request failed",
                data: {
                    code: err?.data?.code,
                    details: err?.data ?? null,
                    requestId,
                    url,
                    method: upperMethod,
                },
            });
        }
    }

    throw lastErr;
}
